import { useAppSelector } from '@hooks/redux.hooks';
import {
  hasSensorLADorPerimeterAccessFromActiveProfile,
  hasSensorRoleFromActiveProfile,
} from '@redux/authent/authent.selectors';
import {
  CategoryEnum,
  LadSensorCategoryMapping,
  PerimeterSensorShorthands,
} from '@utils/sensors/configuration.constants';
import { isEqual } from 'lodash';
import { useReducer } from 'react';

import { ActionEnum } from '@/types/commons/commons.types';
import { LadSensorAbbrKey, PerimeterSensorAbbrKey, SensorInstallationStatusEnum } from '@/types/filters.types';
import { SensorFamilyEnum } from '@/types/sensor/sensor.types';

export enum SortEnum {
  TYPE = 'TYPE',
  STATUS = 'STATUS',
}

export interface FilterState {
  categories: Set<CategoryEnum>;
  ladSensors: Map<LadSensorAbbrKey, boolean>;
  perimeterSensors: Map<PerimeterSensorAbbrKey, boolean>;
  status: Map<SensorInstallationStatusEnum, boolean>;
  sortBy: SortEnum;
}

type Action =
  | { type: 'UPDATE_CATEGORIES'; category: CategoryEnum }
  | { type: 'UPDATE_STATUS'; status: SensorInstallationStatusEnum }
  | { type: 'UPDATE_FILTER_STATE'; category: CategoryEnum; filter: string }
  | { type: 'SET_SORT_BY'; sortBy: SortEnum }
  | { type: 'RESET_FILTERS'; initialState: FilterState };

function filterReducer(state: FilterState, action: Action): FilterState {
  switch (action.type) {
    case 'UPDATE_CATEGORIES': {
      const newCategories = new Set(state.categories);
      newCategories.has(action.category) ? newCategories.delete(action.category) : newCategories.add(action.category);
      const newState = { ...state, categories: newCategories };
      if (action.category === CategoryEnum.LAD) {
        newState.ladSensors = new Map(
          [...state.ladSensors.keys()].map((sensor) => [sensor, newCategories.has(CategoryEnum.LAD)]),
        );
      } else {
        newState.perimeterSensors = new Map(
          [...state.perimeterSensors.keys()].map((sensor) => [sensor, newCategories.has(CategoryEnum.PERIM)]),
        );
      }
      return newState;
    }
    case 'UPDATE_STATUS': {
      return {
        ...state,
        status: new Map(state.status).set(action.status, !state.status.get(action.status)),
      };
    }
    case 'UPDATE_FILTER_STATE': {
      const isLad = action.category === CategoryEnum.LAD;
      const sensors = isLad ? state.ladSensors : state.perimeterSensors;
      const newSensors = new Map(sensors).set(action.filter, !sensors.get(action.filter));
      const newCategories = new Set(state.categories);
      if (Array.from(newSensors.values()).some((value) => !value)) {
        newCategories.delete(action.category);
      } else if (Array.from(newSensors.values()).every(Boolean)) {
        newCategories.add(action.category);
      }
      return {
        ...state,
        categories: newCategories,
        ladSensors: isLad ? newSensors : state.ladSensors,
        perimeterSensors: isLad ? state.perimeterSensors : newSensors,
      };
    }
    case 'SET_SORT_BY':
      return { ...state, sortBy: action.sortBy };
    case 'RESET_FILTERS':
      return action.initialState;
    default:
      return state;
  }
}

export interface UseSensorFilterStateReturn {
  state: FilterState;
  canReset: boolean;
  updateCategories: (category: CategoryEnum) => void;
  updateStatus: (status: SensorInstallationStatusEnum) => void;
  updateFilterState: (category: CategoryEnum, filter: string) => void;
  setSortBy: (sortBy: SortEnum) => void;
  resetFilters: () => void;
}

export const useSensorFilterState = (): UseSensorFilterStateReturn => {
  const canAccessCollaborativeSensor = useAppSelector((state) =>
    hasSensorRoleFromActiveProfile(state, { action: ActionEnum.ACCESS, sensorFamily: SensorFamilyEnum.COLLABORATIVE }),
  );
  const canAccessRFSensor = useAppSelector((state) =>
    hasSensorRoleFromActiveProfile(state, { action: ActionEnum.ACCESS, sensorFamily: SensorFamilyEnum.RF }),
  );
  const canAccessRadarSensor = useAppSelector((state) =>
    hasSensorRoleFromActiveProfile(state, { action: ActionEnum.ACCESS, sensorFamily: SensorFamilyEnum.RADAR }),
  );
  const canAccessCameraSensor = useAppSelector((state) =>
    hasSensorRoleFromActiveProfile(state, { action: ActionEnum.ACCESS, sensorFamily: SensorFamilyEnum.CAMERA }),
  );
  const canAccessNeutralSensor = useAppSelector((state) =>
    hasSensorRoleFromActiveProfile(state, { action: ActionEnum.ACCESS, sensorFamily: SensorFamilyEnum.NEUTRAL }),
  );

  const accessibleSensorsCategories = {
    ...(canAccessCollaborativeSensor && {
      [SensorFamilyEnum.COLLABORATIVE]: LadSensorCategoryMapping[SensorFamilyEnum.COLLABORATIVE],
    }),
    ...(canAccessRFSensor && { [SensorFamilyEnum.RF]: LadSensorCategoryMapping[SensorFamilyEnum.RF] }),
    ...(canAccessRadarSensor && { [SensorFamilyEnum.RADAR]: LadSensorCategoryMapping[SensorFamilyEnum.RADAR] }),
    ...(canAccessCameraSensor && { [SensorFamilyEnum.CAMERA]: LadSensorCategoryMapping[SensorFamilyEnum.CAMERA] }),
    ...(canAccessNeutralSensor && { [SensorFamilyEnum.NEUTRAL]: LadSensorCategoryMapping[SensorFamilyEnum.NEUTRAL] }),
  };
  const displayedLadSensors = Object.values(accessibleSensorsCategories).flat().sort();

  const canAccessPerimeterSensor = useAppSelector((state) =>
    hasSensorLADorPerimeterAccessFromActiveProfile(state, CategoryEnum.PERIM),
  );

  const displayedPerimSensors = canAccessPerimeterSensor ? PerimeterSensorShorthands : [];

  const initialState: FilterState = {
    categories: new Set(),
    ladSensors: new Map(displayedLadSensors.map((sensor) => [sensor, false])),
    perimeterSensors: new Map(displayedPerimSensors.map((sensor) => [sensor, false])),
    status: new Map(Object.values(SensorInstallationStatusEnum).map((status) => [status, true])),
    sortBy: SortEnum.TYPE,
  };

  const [state, dispatch] = useReducer(filterReducer, initialState);

  const updateCategories = (category: CategoryEnum) => {
    dispatch({ type: 'UPDATE_CATEGORIES', category });
  };

  const updateStatus = (status: SensorInstallationStatusEnum) => {
    dispatch({ type: 'UPDATE_STATUS', status });
  };

  const updateFilterState = (category: CategoryEnum, filter: string) => {
    dispatch({ type: 'UPDATE_FILTER_STATE', category, filter });
  };

  const setSortBy = (sortBy: SortEnum) => {
    dispatch({ type: 'SET_SORT_BY', sortBy });
  };

  const resetFilters = () => {
    dispatch({ type: 'RESET_FILTERS', initialState });
  };

  function normalizeFilterState(state: FilterState) {
    const sortEntries = <K, V>(entries: [K, V][]) =>
      entries.toSorted(([a], [b]) => JSON.stringify(a).localeCompare(JSON.stringify(b)));

    const sortArray = <T>(array: T[]) => array.toSorted((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));

    return {
      categories: sortArray(Array.from(state.categories)),
      ladSensors: Object.fromEntries(sortEntries([...state.ladSensors])),
      perimeterSensors: Object.fromEntries(sortEntries([...state.perimeterSensors])),
      status: Object.fromEntries(sortEntries([...state.status])),
      sortBy: state.sortBy,
    };
  }

  const canReset = !isEqual(normalizeFilterState(state), normalizeFilterState(initialState));

  return {
    state,
    canReset,
    updateCategories,
    updateStatus,
    updateFilterState,
    setSortBy,
    resetFilters,
  };
};
